import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Gamepad2, Globe, Gem, Shield, Crown } from 'lucide-react';

export const AboutPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-dark-primary py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        {/* Back Button */}
        <Link to="/" className="inline-flex items-center text-purple-light hover:text-purple-primary transition-colors mb-8">
          <ArrowLeft className="mr-2" size={20} />
          Voltar para Home
        </Link>

        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-green-primary to-purple-primary rounded-full mb-6 animate-float">
            <Gamepad2 className="text-white" size={48} />
          </div>
          <h1 className="text-5xl sm:text-6xl font-bold font-archive text-white mb-6 glow-text">
            Sobre Nós
          </h1>
          <p className="text-2xl sm:text-3xl text-green-primary font-hero font-semibold mb-6">
            Transformando habilidade em reconhecimento.
          </p>
        </div>

        {/* Mission Statement */}
        <div className="bg-dark-secondary border border-purple-primary/20 rounded-2xl p-8 mb-12 text-center">
          <p className="text-xl text-gray-300 font-hero leading-relaxed mb-4">
            A GAMEODYS nasceu com um propósito claro:<br />
            <span className="text-white font-semibold">criar um ambiente onde talento, estratégia e dedicação são valorizados.</span>
          </p>
          <p className="text-lg text-gray-400 font-hero leading-relaxed mb-4">
            Acreditamos que competir é evoluir.<br />
            E que cada vitória merece ser reconhecida.
          </p>
          <p className="text-xl text-purple-light font-hero font-bold">
            Não somos apenas um jogo.<br />
            Somos uma nova forma de viver a competição.
          </p>
        </div>

        {/* Nossa Essência */}
        <div className="mb-12">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-green-primary to-green-secondary rounded-xl flex items-center justify-center">
              <Globe className="text-white" size={32} />
            </div>
            <h2 className="text-4xl font-bold font-archive text-white">Nossa Essência</h2>
          </div>
          <div className="bg-dark-secondary border border-green-primary/20 rounded-2xl p-8">
            <p className="text-lg text-gray-300 font-hero leading-relaxed mb-4">
              Criamos experiências competitivas justas, seguras e acessíveis, onde jogadores podem evoluir, disputar e conquistar reconhecimento real.
            </p>
            <p className="text-lg text-gray-300 font-hero leading-relaxed mb-2">
              Na GAMEODYS, <span className="text-green-primary font-semibold">cada vitória fortalece sua jornada</span>.
            </p>
            <p className="text-lg text-gray-300 font-hero leading-relaxed mb-4">
              <span className="text-green-primary font-semibold">Cada desafio impulsiona sua evolução</span>.
            </p>
            <p className="text-lg text-gray-300 font-hero leading-relaxed">
              Acreditamos na força da comunidade.<br />
              Na energia da competição.<br />
              Na emoção de conquistar.
            </p>
          </div>
        </div>

        {/* Nosso Ecossistema */}
        <div className="mb-12">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-primary to-purple-light rounded-xl flex items-center justify-center">
              <Gem className="text-white" size={32} />
            </div>
            <h2 className="text-4xl font-bold font-archive text-white">Nosso Ecossistema</h2>
          </div>
          <div className="bg-dark-secondary border border-purple-primary/20 rounded-2xl p-8">
            <p className="text-lg text-gray-300 font-hero leading-relaxed mb-6">
              Oferecemos diferentes formas de jogar:
            </p>
            <div className="space-y-4 mb-6">
              <div className="bg-dark-primary/50 p-6 rounded-xl border-l-4 border-green-primary">
                <p className="text-lg text-white font-hero">
                  <span className="text-green-primary font-bold">•</span> Partidas gratuitas para quem quer evoluir e se divertir
                </p>
              </div>
              <div className="bg-dark-primary/50 p-6 rounded-xl border-l-4 border-purple-primary">
                <p className="text-lg text-white font-hero">
                  <span className="text-purple-light font-bold">•</span> Partidas competitivas para quem deseja testar sua habilidade em um novo nível
                </p>
              </div>
            </div>
            <p className="text-lg text-gray-300 font-hero leading-relaxed mb-2">
              Aqui, <span className="text-purple-light font-semibold">o jogador escolhe como quer competir</span>.
            </p>
            <p className="text-lg text-gray-400 font-hero leading-relaxed">
              O importante é que cada partida tenha propósito.
            </p>
          </div>
        </div>

        {/* Nosso Compromisso */}
        <div className="mb-12">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-green-primary to-purple-primary rounded-xl flex items-center justify-center">
              <Shield className="text-white" size={32} />
            </div>
            <h2 className="text-4xl font-bold font-archive text-white">Nosso Compromisso</h2>
          </div>
          <div className="bg-dark-secondary border border-green-primary/20 rounded-2xl p-8">
            <p className="text-lg text-gray-300 font-hero leading-relaxed mb-6">
              Trabalhamos continuamente para manter:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-dark-primary/50 p-4 rounded-xl text-center">
                <p className="text-green-primary font-hero font-semibold">Ambiente seguro e transparente</p>
              </div>
              <div className="bg-dark-primary/50 p-4 rounded-xl text-center">
                <p className="text-purple-light font-hero font-semibold">Sistema equilibrado e justo</p>
              </div>
              <div className="bg-dark-primary/50 p-4 rounded-xl text-center">
                <p className="text-green-primary font-hero font-semibold">Comunidade ativa e respeitosa</p>
              </div>
            </div>
            <p className="text-lg text-gray-300 font-hero leading-relaxed">
              Estamos construindo mais do que uma plataforma.<br />
              <span className="text-white font-semibold">Estamos formando um espaço onde competir significa crescer.</span>
            </p>
          </div>
        </div>

        {/* Final CTA */}
        <div className="bg-gradient-to-r from-purple-primary via-green-primary to-purple-primary p-1 rounded-2xl">
          <div className="bg-dark-primary rounded-2xl p-12 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-primary to-purple-primary rounded-full mb-6">
              <Crown className="text-white" size={40} />
            </div>
            <h2 className="text-4xl font-bold font-archive text-white mb-4">
              GAMEODYS
            </h2>
            <p className="text-2xl text-green-primary font-hero font-bold mb-2">
              Quem joga, evolui.
            </p>
            <p className="text-2xl text-purple-light font-hero font-bold">
              Quem evolui, se destaca.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
